/**
 * Iter 25 (D1) — Live template preview pane.
 *
 * Shows 3-4 sample turns of a template's behaviour with the same
 * typing/streaming UX as Sam. We deliberately do this client-side via
 * `setInterval` rather than spinning up a new backend SSE endpoint —
 * the goal is "show the user what the template feels like" and a
 * fake but realistic transcript hits that with zero infra cost,
 * zero LLM spend, and a deterministic UX (no model variance). When
 * the user clicks "Use this template" they get the real thing.
 *
 * Pre-canned transcripts live in DEMO_TURNS_BY_TEMPLATE below.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles } from "lucide-react";

const DEMO_TURNS_BY_TEMPLATE = {
  support: [
    { role: "user", text: "I need to reset my password." },
    {
      role: "assistant",
      text:
        "Sure! Click \"Sign in\" up top, then \"Forgot password\". We'll email " +
        "you a reset link in under a minute. If it doesn't land in 5 minutes, " +
        "check spam or drop us a line at support@symplee.ai and a real human " +
        "will help.",
    },
    { role: "user", text: "Thanks — is there a charge for that?" },
    {
      role: "assistant",
      text:
        "Not at all. Password resets and account help are free for every " +
        "plan, no support tier required.",
    },
  ],
  qualifier: [
    { role: "user", text: "I'm exploring options. What makes you different?" },
    {
      role: "assistant",
      text:
        "Happy to share — and a couple of quick questions first, so I show " +
        "you what's most relevant:\n\n" +
        "1) How big is your team today?\n" +
        "2) Are you running outreach manually, or already using a CRM?\n\n" +
        "Once I know, I'll line up a 20-minute walkthrough with the most " +
        "useful modules first.",
    },
    { role: "user", text: "Team of 4. Mostly manual outreach via Gmail." },
    {
      role: "assistant",
      text:
        "Perfect fit. For a team your size, I'd start with Smart Leads + " +
        "Outreach Sequences (replaces ~6 hrs/week of copy-paste). Want me to " +
        "book a 20-min demo this Thursday or next Tuesday?",
    },
  ],
  writer: [
    { role: "user", text: "Draft a LinkedIn post about our new feature." },
    {
      role: "assistant",
      text:
        "Try this hook:\n\n" +
        "We just shipped grounded AI agents — and it's the feature I've been " +
        "waiting on for two years.\n\n" +
        "Here's what it means for you in practice:\n" +
        "→ Every reply quotes YOUR docs, not generic web copy.\n" +
        "→ Embed a chatbot on your site in 30 seconds.\n" +
        "→ No rebuild, no contracts, $19/mo with FOUNDER19.\n\n" +
        "If you've ever cringed at an \"AI assistant\" sounding nothing like " +
        "your business, this is for you. Link in the first comment.",
    },
    { role: "user", text: "Make it shorter, more direct." },
    {
      role: "assistant",
      text:
        "Tighter version:\n\n" +
        "Grounded AI is shipping today.\n\n" +
        "Every reply quotes YOUR docs.\n" +
        "Embed in 30 seconds. No rebuild.\n" +
        "$19/mo with FOUNDER19.\n\n" +
        "Link below.",
    },
  ],
  internal: [
    { role: "user", text: "What's our refund policy?" },
    {
      role: "assistant",
      text:
        "Full refund within 14 days of first payment, no questions. After " +
        "that, we pro-rate the unused portion of the current month. Source: " +
        "Pricing & Promises.md.",
    },
    { role: "user", text: "And what's the price for additional seats?" },
    {
      role: "assistant",
      text:
        "$9/month per additional seat, billed on the same monthly cycle as " +
        "the base $29 owner seat. Source: Pricing & Promises.md.",
    },
  ],
};

// Typing speed — fast enough to feel snappy, slow enough to register as
// "streaming" rather than instant. Tuned via screenshot review.
const CHAR_INTERVAL_MS = 14;
const TURN_GAP_MS = 350;

export function TemplatePreviewPane({ template, onBack, onUseTemplate }) {
  const turns = useMemo(
    () => DEMO_TURNS_BY_TEMPLATE[template.id] || [],
    [template.id],
  );
  // `rendered[i]` = number of chars already typed for the i-th turn.
  const [rendered, setRendered] = useState([]);
  const [done, setDone] = useState(false);
  const cancelRef = useRef(false);

  // Drive the streaming animation. Re-runs whenever template changes (i.e.
  // a different preview opens). We deliberately re-implement the typing
  // animation here rather than reusing Chat's because the dependencies
  // (auth, models, conversation state) are unnecessary for a demo pane.
  useEffect(() => {
    cancelRef.current = false;
    setRendered(turns.map((_, i) => (i === 0 && turns[0].role === "user" ? turns[0].text.length : 0)));
    setDone(false);
    let turnIdx = 0;
    let charIdx = 0;
    // User messages render instantly; only assistant turns "stream".
    if (turns[0]?.role === "user") {
      turnIdx = 1;
    }
    const tick = () => {
      if (cancelRef.current) return;
      if (turnIdx >= turns.length) {
        setDone(true);
        return;
      }
      const turn = turns[turnIdx];
      if (turn.role === "user") {
        // Reveal the user's question all at once, then continue.
        setRendered((prev) => {
          const next = [...prev];
          next[turnIdx] = turn.text.length;
          return next;
        });
        turnIdx += 1;
        charIdx = 0;
        setTimeout(tick, TURN_GAP_MS);
        return;
      }
      // Assistant turn — type one char at a time.
      charIdx += 1;
      setRendered((prev) => {
        const next = [...prev];
        next[turnIdx] = charIdx;
        return next;
      });
      if (charIdx >= turn.text.length) {
        turnIdx += 1;
        charIdx = 0;
        setTimeout(tick, TURN_GAP_MS);
        return;
      }
      setTimeout(tick, CHAR_INTERVAL_MS);
    };
    const startId = setTimeout(tick, 250);
    return () => {
      cancelRef.current = true;
      clearTimeout(startId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template?.id]);

  const visibleTurns = useMemo(() => {
    return turns.map((t, i) => ({
      ...t,
      visibleText: t.text.slice(0, rendered[i] || 0),
      typing: !done && i === rendered.findIndex((v, idx) => v < turns[idx].text.length && idx >= 0),
    }));
  }, [turns, rendered, done]);

  return (
    <div className="space-y-3" data-testid={`template-preview-${template.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-xs text-white/55 hover:text-white"
            data-testid="template-preview-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All templates
          </button>
          <span className="text-xs text-white/35">·</span>
          <span className="text-sm font-medium text-white">{template.name} preview</span>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-500/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-fuchsia-200">
          <Sparkles className="h-3 w-3" /> Live demo
        </span>
      </div>

      <div
        className="max-h-[380px] space-y-3 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.02] p-4"
        data-testid="template-preview-transcript"
      >
        {visibleTurns.map((t, i) => {
          // Iter 25 polish — don't render bubbles for turns that haven't
          // started yet; an empty pill mid-conversation looks like a
          // dropped message.
          const hasStarted = (rendered[i] || 0) > 0;
          if (!hasStarted) return null;
          return (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                t.role === "user"
                  ? "bg-white/10 text-white"
                  : "bg-fuchsia-500/[0.08] text-white/90 ring-1 ring-fuchsia-400/15"
              }`}
              data-testid={`template-preview-turn-${i}`}
            >
              {t.visibleText}
              {t.typing && t.role === "assistant" && (
                <span className="ml-0.5 inline-block h-3 w-[2px] animate-pulse bg-fuchsia-300" aria-hidden />
              )}
            </div>
          </motion.div>
          );
        })}
      </div>

      <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[11px] text-white/45">
          Demo conversation — sample replies. With your own KB attached, every answer cites your docs.
        </p>
        <Button
          onClick={() => onUseTemplate(template)}
          className="rounded-xl bg-white text-black hover:bg-zinc-200"
          data-testid="template-preview-use-template"
        >
          Use this template
        </Button>
      </div>
    </div>
  );
}
